/**
 * Pure editing operations: each returns the Ops for one undoable transaction.
 * No UI state here, so everything is unit-testable.
 */
import {
  applyTransaction,
  newId,
  nextTag,
  removeElementOps,
  type Element,
  type Id,
  type Op,
  type Point,
  type Project,
  type Rotation,
  type Wire,
  type WireEnd,
} from '@elec/domain';
import { wireEndPoint } from '@elec/renderer';
import { elementBounds, getSymbol, portDirection, type SymbolDef } from '@elec/symbols';
import { route, segmentDir, simplify, snap, type Dir } from './routing.ts';

const portDirOf = (project: Project, end: WireEnd): Dir | null => {
  if (end.kind !== 'port') return null;
  const el = project.elements[end.elementId];
  const def = el && getSymbol(el.symbolId);
  const port = def?.ports.find((p) => p.id === end.portId);
  return el && port ? portDirection(port, el) : null;
};

const attachedTo = (w: Wire, ids: ReadonlySet<Id>) => ({
  from: w.from.kind === 'port' && ids.has(w.from.elementId),
  to: w.to.kind === 'port' && ids.has(w.to.elementId),
});

const opposite: Record<Dir, Dir> = { up: 'down', down: 'up', left: 'right', right: 'left' };

/** Re-route a wire from scratch between its (current) end points. */
export function rerouteVia(project: Project, w: Wire): Point[] {
  const a = wireEndPoint(project, w.from);
  const b = wireEndPoint(project, w.to);
  if (!a || !b) return w.via;
  return route(a, portDirOf(project, w.from), b, portDirOf(project, w.to));
}

/**
 * Keep a wire orthogonal after one of its ends moved by (dx, dy): shift the neighbouring
 * corner along with it, or insert corners when the wire was a single straight segment.
 */
function followEnd(
  before: Project,
  after: Project,
  w: Wire,
  which: 'from' | 'to',
  dx: number,
  dy: number,
): Point[] {
  const via = w.via.map((p) => ({ ...p }));
  if (via.length === 0) {
    const a = wireEndPoint(after, w.from);
    const b = wireEndPoint(after, w.to);
    if (!a || !b) return via;
    return route(a, portDirOf(after, w.from), b, portDirOf(after, w.to));
  }
  const endBefore = wireEndPoint(before, w[which]);
  const idx = which === 'from' ? 0 : via.length - 1;
  const v = via[idx]!;
  if (!endBefore) return via;
  const d = segmentDir(endBefore, v);
  if (d === 'up' || d === 'down') v.x += dx;
  else if (d === 'left' || d === 'right') v.y += dy;
  else {
    // The end sat on its corner: keep it there.
    v.x += dx;
    v.y += dy;
  }
  return via;
}

/** Move elements and wires by (dx, dy); attached wires follow and stay orthogonal. */
export function moveOps(project: Project, ids: Iterable<Id>, dx: number, dy: number): Op[] {
  const sel = new Set(ids);
  const elIds = new Set([...sel].filter((id) => project.elements[id]));
  if (dx === 0 && dy === 0) return [];
  const ops: Op[] = [];
  for (const id of elIds) {
    const e = project.elements[id]!;
    ops.push({ op: 'update', coll: 'elements', id, patch: { x: e.x + dx, y: e.y + dy } });
  }
  const after = applyTransaction(project, { label: '', ops }).project;
  for (const w of Object.values(project.wires)) {
    const att = attachedTo(w, elIds);
    const whole = sel.has(w.id) || (att.from && att.to);
    if (whole) {
      const shift = (e: WireEnd): WireEnd =>
        e.kind === 'point' ? { kind: 'point', x: e.x + dx, y: e.y + dy } : e;
      ops.push({
        op: 'update',
        coll: 'wires',
        id: w.id,
        patch: {
          from: shift(w.from),
          to: shift(w.to),
          via: w.via.map((p) => ({ x: p.x + dx, y: p.y + dy })),
        },
      });
    } else if (att.from || att.to) {
      let via = w.via;
      if (att.from) via = followEnd(project, after, { ...w, via }, 'from', dx, dy);
      if (att.to) via = followEnd(project, after, { ...w, via }, 'to', dx, dy);
      const a = wireEndPoint(after, w.from)!;
      const b = wireEndPoint(after, w.to)!;
      ops.push({
        op: 'update',
        coll: 'wires',
        id: w.id,
        patch: { via: simplify([a, ...via, b]).slice(1, -1) },
      });
    }
  }
  return ops;
}

/** Delete elements (with their wires) and wires. */
export function deleteOps(project: Project, ids: Iterable<Id>): Op[] {
  const ops: Op[] = [];
  let p = project;
  const removedWires = new Set<Id>();
  for (const id of ids) {
    if (p.elements[id]) {
      const o = removeElementOps(p, id);
      for (const op of o) if (op.op === 'remove' && op.coll === 'wires') removedWires.add(op.id);
      p = applyTransaction(p, { label: '', ops: o }).project;
      ops.push(...o);
    } else if (p.wires[id] && !removedWires.has(id)) {
      const o: Op = { op: 'remove', coll: 'wires', id };
      p = applyTransaction(p, { label: '', ops: [o] }).project;
      ops.push(o);
    }
  }
  return ops;
}

function selectionCenter(project: Project, ids: Iterable<Id>): Point | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const id of ids) {
    const e = project.elements[id];
    const def = e && getSymbol(e.symbolId);
    if (!e || !def) continue;
    const b = elementBounds(def, e);
    minX = Math.min(minX, b.minX);
    minY = Math.min(minY, b.minY);
    maxX = Math.max(maxX, b.maxX);
    maxY = Math.max(maxY, b.maxY);
  }
  return minX === Infinity ? null : { x: snap((minX + maxX) / 2), y: snap((minY + maxY) / 2) };
}

type PointMap = (p: Point) => Point;

/** Apply a rigid transform (rotation by 90° or mirror) to the selection. */
function transformSelection(
  project: Project,
  ids: Iterable<Id>,
  map: PointMap,
  elementPatch: (e: Element, def: SymbolDef, center: Point) => Partial<Element>,
): Op[] {
  const sel = new Set(ids);
  const elIds = new Set([...sel].filter((id) => project.elements[id]));
  const ops: Op[] = [];
  for (const id of elIds) {
    const e = project.elements[id]!;
    const def = getSymbol(e.symbolId);
    if (!def) continue;
    const c = map({ x: e.x + def.width / 2, y: e.y + def.height / 2 });
    ops.push({ op: 'update', coll: 'elements', id, patch: elementPatch(e, def, c) });
  }
  const after = applyTransaction(project, { label: '', ops }).project;
  for (const w of Object.values(project.wires)) {
    const att = attachedTo(w, elIds);
    if (sel.has(w.id) || (att.from && att.to)) {
      const m = (e: WireEnd): WireEnd => (e.kind === 'point' ? { kind: 'point', ...map(e) } : e);
      ops.push({
        op: 'update',
        coll: 'wires',
        id: w.id,
        patch: { from: m(w.from), to: m(w.to), via: w.via.map(map) },
      });
    } else if (att.from || att.to) {
      ops.push({ op: 'update', coll: 'wires', id: w.id, patch: { via: rerouteVia(after, w) } });
    }
  }
  return ops;
}

/** Rotate the selection 90° clockwise about its centre. */
export function rotateOps(project: Project, ids: Iterable<Id>): Op[] {
  const list = [...ids];
  const c = selectionCenter(project, list);
  if (!c) return [];
  const map: PointMap = (p) => ({ x: c.x - (p.y - c.y), y: c.y + (p.x - c.x) });
  return transformSelection(project, list, map, (e, def, nc) => ({
    x: nc.x - def.width / 2,
    y: nc.y - def.height / 2,
    rotation: ((e.rotation + 90) % 360) as Rotation,
  }));
}

/** Mirror the selection left ↔ right about its centre. */
export function mirrorOps(project: Project, ids: Iterable<Id>): Op[] {
  const list = [...ids];
  const c = selectionCenter(project, list);
  if (!c) return [];
  const map: PointMap = (p) => ({ x: 2 * c.x - p.x, y: p.y });
  return transformSelection(project, list, map, (e, def, nc) => {
    // Mirroring a rotated symbol horizontally = toggle mirror + reflect rotation.
    const rotation = ((360 - e.rotation) % 360) as Rotation;
    return { x: nc.x - def.width / 2, y: nc.y - def.height / 2, mirrored: !e.mirrored, rotation };
  });
}

// ---------- clipboard ----------

export interface ClipboardData {
  app: 'tablo' | 'elec';
  kind: 'selection';
  elements: Element[];
  wires: Wire[];
}

/** Copy elements and wires. Wire ends attached to elements outside the copy become free points. */
export function copySelection(project: Project, ids: Iterable<Id>): ClipboardData {
  const sel = new Set(ids);
  const elements = [...sel].map((id) => project.elements[id]).filter((e): e is Element => !!e);
  const elIds = new Set(elements.map((e) => e.id));
  const wires: Wire[] = [];
  for (const w of Object.values(project.wires)) {
    const att = attachedTo(w, elIds);
    if (!sel.has(w.id) && !(att.from && att.to)) continue;
    const free = (e: WireEnd): WireEnd => {
      if (e.kind === 'point' || elIds.has(e.elementId)) return e;
      const p = wireEndPoint(project, e) ?? { x: 0, y: 0 };
      return { kind: 'point', x: p.x, y: p.y };
    };
    wires.push({ ...w, from: free(w.from), to: free(w.to) });
  }
  return { app: 'tablo', kind: 'selection', elements, wires };
}

export function isClipboardData(v: unknown): v is ClipboardData {
  return (
    !!v &&
    typeof v === 'object' &&
    ['tablo', 'elec'].includes((v as ClipboardData).app) &&
    Array.isArray((v as ClipboardData).elements)
  );
}

/**
 * Paste onto a sheet, offset by (dx, dy). New ids; tags that already exist are renumbered.
 * Returns the ops and the ids of the pasted items (to select them).
 */
export function pasteOps(
  project: Project,
  data: ClipboardData,
  sheetId: Id,
  dx: number,
  dy: number,
): { ops: Op[]; ids: Id[] } {
  const ops: Op[] = [];
  const ids: Id[] = [];
  const remap = new Map<Id, Id>();
  let working = project;
  const usedTags = new Set(Object.values(project.elements).map((e) => e.tag));
  for (const e of data.elements) {
    const id = newId();
    remap.set(e.id, id);
    let tag = e.tag;
    const def = getSymbol(e.symbolId);
    if (tag && usedTags.has(tag)) tag = nextTag(working, def?.tagPrefix ?? tag.replace(/\d+$/, ''));
    usedTags.add(tag);
    const value: Element = { ...e, id, sheetId, tag, x: e.x + dx, y: e.y + dy, props: { ...e.props } };
    if (value.circuitId && !project.circuits[value.circuitId]) delete value.circuitId;
    ops.push({ op: 'add', coll: 'elements', value });
    working = { ...working, elements: { ...working.elements, [id]: value } };
    ids.push(id);
  }
  const end = (e: WireEnd): WireEnd =>
    e.kind === 'point'
      ? { kind: 'point', x: e.x + dx, y: e.y + dy }
      : { ...e, elementId: remap.get(e.elementId) ?? e.elementId };
  for (const w of data.wires) {
    const id = newId();
    ops.push({
      op: 'add',
      coll: 'wires',
      value: {
        ...w,
        id,
        sheetId,
        from: end(w.from),
        to: end(w.to),
        via: w.via.map((p) => ({ x: p.x + dx, y: p.y + dy })),
      },
    });
    ids.push(id);
  }
  return { ops, ids };
}

/** Centre of clipboard content (for pasting under the cursor). */
export function clipboardCenter(data: ClipboardData): Point {
  const xs: number[] = [];
  const ys: number[] = [];
  for (const e of data.elements) {
    const def = getSymbol(e.symbolId);
    if (!def) continue;
    const b = elementBounds(def, e);
    xs.push(b.minX, b.maxX);
    ys.push(b.minY, b.maxY);
  }
  for (const w of data.wires)
    for (const p of [w.from, w.to, ...w.via]) {
      if ('x' in p) {
        xs.push(p.x);
        ys.push(p.y);
      }
    }
  if (!xs.length) return { x: 0, y: 0 };
  return { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: (Math.min(...ys) + Math.max(...ys)) / 2 };
}

export { opposite };
